import {Typography, Box, Button, Switch} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faXmark} from '@fortawesome/free-solid-svg-icons';
import {mainLinearGradient} from '../../theme/gradients';

/**
 * CustomSwitch component for rendering a customized switch.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @returns {JSX.Element} The CustomSwitch component.
 */
export const CustomSwitch = props => {
  const styles = {
    width: 44,
    height: 24,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(20px)',
        color: '#fff',
        //when it is to the right
        '& + .MuiSwitch-track': {
          backgroundColor: '#31A1F3',
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        border: '6px solid #fff',
      },
    },
    '& .MuiSwitch-thumb': {
      transform: 'translateY(1px) translateX(1px)',
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    // when it is to the left
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: '#31A1F33B',
      opacity: 1,
    },
  };
  return <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple sx={styles} {...props} />;
};

const pricingCardLayoutStyles = {
  width: '100%',
  height: '100%',
  background: 'white',
  borderRadius: 5,
  border: '4px solid transparent',
  boxShadow: '0px 6px 8px 3px rgba(0, 0, 0, 0.25);',

  py: {xs: '18px', sm: '30px'},
  px: {xs: '12px', sm: '25px'},

  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: {
    xs: 'column',
    md: 'row',
    lg: 'column',
  },
  gap: {
    xs: 1,
    sm: 3,
    md: 3,
    lg: 1,
  },
};

const linkButtonStyles = {
  //spacing
  // mt: 1,
  //button
  background: mainLinearGradient,
  borderRadius: 6,
  width: '100%',
  //text
  color: '#FFF',
  fontSize: 20,
  fontWeight: 700,
};
const isUserLinkButtonStyles = {
  //spacing
  // mt: 1,
  //button
  border: '3px solid transparent',
  borderRadius: 6,
  width: '100%',
  //text
  color: mainLinearGradient,
  fontSize: 20,
  fontWeight: 700,
};

const IconAndText = ({text, goodFeature}) => {
  const icon = goodFeature ? (
    <FontAwesomeIcon icon={faCheck} style={{color: '#36C6CB', fontSize: 20}} />
  ) : (
    <FontAwesomeIcon icon={faXmark} style={{color: 'red', fontSize: 20}} />
  );
  return (
    <Typography sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
      {icon}
      {text}
    </Typography>
  );
};

const cardFirstPartProps = {
  className: 'firstPart',
  sx: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: {
      xs: '0px',
      md: '500px',
      lg: '0px',
    },
    gap: 1,
    height: {
      xs: undefined,
      lg: 255,
    },
  },
};
const cardSecondPartProps = {
  className: 'secondPart',
  sx: {
    mt: {
      xs: 2,
      md: 0,
    },
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    width: {xs: undefined, md: 800, lg: '100%'},
  },
};

/**
 * BasicPlanCard component for rendering the basic plan card.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {boolean} props.isCurrentUserPlan - Indicates if the current user is on this plan.
 * @param {Function} props.onClick - Function to handle the button click.
 * @returns {JSX.Element} The BasicPlanCard component.
 */
export const BasicPlanCard = ({isCurrentUserPlan, onClick}) => {
  return (
    <Box sx={pricingCardLayoutStyles}>
      <Box {...cardFirstPartProps}>
        <Box className="planAndPriceText" sx={{height: {xs: undefined, md: 200}}}>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 32,
              fontWeight: 700,
            }}
          >
            Basic
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 60,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            $0
          </Typography>
          <Typography sx={{textAlign: 'center'}}>monthly</Typography>
        </Box>
        <Button
          sx={isCurrentUserPlan ? isUserLinkButtonStyles : linkButtonStyles}
          onClick={isCurrentUserPlan ? () => {} : onClick}
        >
          {isCurrentUserPlan ? 'You are on this plan' : 'Go back to basic plan'}
        </Button>
      </Box>
      <Box {...cardSecondPartProps}>
        <IconAndText goodFeature text={'Basic symptom screening'} />
        <IconAndText goodFeature text={'6 health metrics face scan'} />
        <IconAndText goodFeature={false} text={'Advaced symptom checking'} />
        <IconAndText goodFeature={false} text={'Track your health progress'} />
        <Typography sx={{fontWeight: 600}}>Best for:</Typography>
        <Typography>
          Health-conscious individuals seeking quick, preliminary assessments of potential medical symptoms.
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * PremiumPlanCard component for rendering the premium plan card.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {boolean} props.switchValue - The value of the switch.
 * @param {Function} props.switchOnChange - Function to handle the switch change.
 * @param {number} props.price - The price of the premium plan.
 * @param {number} props.yearlyPrice - The yearly price (discounted if promo applied).
 * @param {number} props.originalYearlyPrice - The original yearly price before discount.
 * @param {boolean} props.hasDiscount - Whether a discount is applied.
 * @param {boolean} props.isCurrentUserPlan - Indicates if the current user is on this plan.
 * @param {Function} props.onClick - Function to handle the button click.
 * @param {boolean} props.disabled - Indicates if the card is disabled.
 * @returns {JSX.Element} The PremiumPlanCard component.
 */
// In PremiumPlanCard component - fix the yearly price calculation
export const PremiumPlanCard = ({
  switchValue,
  switchOnChange,
  price,
  yearlyPrice, // This should be the discounted MONTHLY price for yearly billing
  originalYearlyPrice, // This should be the original MONTHLY price for yearly billing
  hasDiscount,
  isCurrentUserPlan,
  onClick,
  disabled,
}) => {
  // Calculate the actual yearly totals for display
  const displayYearlyTotal = yearlyPrice ? yearlyPrice * 12 : price * 12;
  const originalYearlyTotal = originalYearlyPrice ? originalYearlyPrice * 12 : undefined;
  const showOriginalPrice = hasDiscount && originalYearlyTotal;

  return (
    <Box sx={pricingCardLayoutStyles}>
      <Box {...cardFirstPartProps}>
        <Box className="planAndPriceText" sx={{height: {xs: undefined, md: 200}}}>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 32,
              fontWeight: 700,
            }}
          >
            Premium
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 60,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            ${price}
            {!switchValue && (
              <span
                style={{
                  fontSize: '20px',
                  fontWeight: 400,
                }}
              >
                {' '}
                /mo
              </span>
            )}
          </Typography>
          {switchValue && (
            <Box sx={{textAlign: 'center'}}>
              {/* Show original yearly total crossed out if discount is applied */}
              {showOriginalPrice && (
                <Typography
                  fontSize="18px"
                  sx={{
                    textDecoration: 'line-through',
                    color: '#6B7280',
                    fontWeight: 400,
                    mb: 0.5,
                  }}
                >
                  ${originalYearlyTotal.toFixed(0)} /yr
                </Typography>
              )}
              <Typography fontSize="20px" fontWeight={700}>
                ${displayYearlyTotal.toFixed(0)}
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  {' '}
                  /yr billed annually
                </span>
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              my: 1,
              mb: 0,
            }}
          >
            <Typography>monthly</Typography>
            <CustomSwitch onChange={switchOnChange} checked={switchValue} disabled={disabled} />
            <Box>
              <Typography>anually</Typography>
              <Typography color="#31A1F3" fontWeight={700}>
                save 27%
              </Typography>
            </Box>
          </Box>
        </Box>
        <Button
          sx={isCurrentUserPlan ? {...isUserLinkButtonStyles, mt: 0} : {...linkButtonStyles, mt: 0}}
          onClick={isCurrentUserPlan ? () => {} : onClick}
        >
          {isCurrentUserPlan ? 'You are on this plan' : 'Upgrade to premium'}
        </Button>
      </Box>
      <Box {...cardSecondPartProps}>
        <IconAndText goodFeature text={'Basic symptom screening'} />
        <IconAndText goodFeature text={'20 health metrics face scan'} />
        <IconAndText goodFeature text={'Upgrade to advanced symptom screening'} />
        <IconAndText goodFeature text={'Track your health progress(coming soon)'} />
        <Typography sx={{fontWeight: 600}}>Best for:</Typography>
        <Typography>
          Health-focused people looking to use our advanced features for a more detailed analysis and tracking of their
          symptom checker results over time.
        </Typography>
      </Box>
    </Box>
  );
};
